// generated with @7nohe/openapi-react-query-codegen@1.6.2 

import { type QueryClient } from "@tanstack/react-query";
import { DefaultService } from "../requests/services.gen";
import { CandleField, CandleFilter, CandleOption, HiroStreamString, IntradayBarType, IntradayGreek, MarketActorKind, OptionAccumulationType, OptionOrigin, Order } from "../requests/types.gen";
import * as Common from "./common";
/**
* The handler for the auth request which generates a token to validate based on basic client
* ID/secret authentication
* @param data The data for the request.
* @param data.authorization Basic Authentication
* @returns string Success
* @throws ApiError
*/
export const prefetchUseDefaultServiceGetAuth = (queryClient: QueryClient, { authorization }: {
  authorization: string;
}) => queryClient.prefetchQuery({ queryKey: Common.UseDefaultServiceGetAuthKeyFn({ authorization }), queryFn: () => DefaultService.getAuth({ authorization }) });
/**
* @param data The data for the request.
* @param data.sym
* @param data.stream
* @param data.field
* @param data.option
* @param data.filter
* @param data.limit
* @param data.start start timestamp in milliseconds
* @param data.end end timestamp in milliseconds
* @returns string Success
* @throws ApiError
*/
export const prefetchUseDefaultServiceGetCandles = (queryClient: QueryClient, { end, field, filter, limit, option, start, stream, sym }: {
  end?: number;
  field?: CandleField;
  filter?: CandleFilter;
  limit?: number;
  option?: CandleOption;
  start?: number;
  stream?: HiroStreamString;
  sym: string;
}) => queryClient.prefetchQuery({ queryKey: Common.UseDefaultServiceGetCandlesKeyFn({ end, field, filter, limit, option, start, stream, sym }), queryFn: () => DefaultService.getCandles({ end, field, filter, limit, option, start, stream, sym }) });
/**
* @param data The data for the request.
* @param data.symbol
* @param data.optionAccumulationType
* @param data.optionOrigin
* @param data.isFiltered
* @param data.candleDurationMs duration of candles. Must be a multiple of 5000
* @param data.timeGte ISO 8601 formatted date-time string like "2025-01-01T05:01:17"
* @param data.timeLt ISO 8601 formatted date-time string like "2025-01-01T05:01:17"
* @param data.limit max number of items to return. Default 100, max 20_000
* @returns DeltaSignalRsp Success
* @throws ApiError
*/
export const prefetchUseDefaultServiceGetDeltaSignal = (queryClient: QueryClient, { candleDurationMs, isFiltered, limit, optionAccumulationType, optionOrigin, symbol, timeGte, timeLt }: {
  candleDurationMs: number;
  isFiltered: boolean;
  limit?: number;
  optionAccumulationType: OptionAccumulationType;
  optionOrigin: OptionOrigin;
  symbol: string;
  timeGte?: string;
  timeLt?: string;
}) => queryClient.prefetchQuery({ queryKey: Common.UseDefaultServiceGetDeltaSignalKeyFn({ candleDurationMs, isFiltered, limit, optionAccumulationType, optionOrigin, symbol, timeGte, timeLt }), queryFn: () => DefaultService.getDeltaSignal({ candleDurationMs, isFiltered, limit, optionAccumulationType, optionOrigin, symbol, timeGte, timeLt }) });
/**
* @param data The data for the request.
* @param data.symbol
* @param data.candleDurationMs duration of candles. Must be a multiple of 5000
* @param data.timeGte ISO 8601 formatted date-time string like "2025-01-01T05:01:17"
* @param data.timeLt ISO 8601 formatted date-time string like "2025-01-01T05:01:17"
* @param data.limit max number of items to return. Default 100, max 20_000
* @param data.order order the candles are fetched and returned in. Default "asc"
* @returns CandlesRsp Success
* @throws ApiError
*/
export const prefetchUseDefaultServiceGetPrices = (queryClient: QueryClient, { candleDurationMs, limit, order, symbol, timeGte, timeLt }: {
  candleDurationMs: number;
  limit?: number;
  order?: Order;
  symbol: string;
  timeGte?: string;
  timeLt?: string;
}) => queryClient.prefetchQuery({ queryKey: Common.UseDefaultServiceGetPricesKeyFn({ candleDurationMs, limit, order, symbol, timeGte, timeLt }), queryFn: () => DefaultService.getPrices({ candleDurationMs, limit, order, symbol, timeGte, timeLt }) });
/**
* Fetches the list of available instruments for the day
* @returns Manifest
* @throws ApiError
*/
export const prefetchUseDefaultServiceGetSgManifest = (queryClient: QueryClient) => queryClient.prefetchQuery({ queryKey: Common.UseDefaultServiceGetSgManifestKeyFn(), queryFn: () => DefaultService.getSgManifest() });
/**
* @param data The data for the request.
* @param data.filters
* @param data.sorting
* @param data.offset
* @param data.limit
* @returns ContractRow Success
* @throws ApiError
*/
export const prefetchUseDefaultServiceGetSgTnsContracts = (queryClient: QueryClient, { filters, limit, offset, sorting }: {
  filters?: string;
  limit?: number;
  offset?: number;
  sorting?: string;
} = {}) => queryClient.prefetchQuery({ queryKey: Common.UseDefaultServiceGetSgTnsContractsKeyFn({ filters, limit, offset, sorting }), queryFn: () => DefaultService.getSgTnsContracts({ filters, limit, offset, sorting }) });
/**
* The handler for the Time & Sales feed
* @param data The data for the request.
* @param data.filters
* @param data.sorting
* @param data.offset
* @param data.limit
* @returns string Success
* @throws ApiError
*/
export const prefetchUseDefaultServiceGetSgTnsFeed = (queryClient: QueryClient, { filters, limit, offset, sorting }: {
  filters?: string;
  limit?: number;
  offset?: number;
  sorting?: string;
} = {}) => queryClient.prefetchQuery({ queryKey: Common.UseDefaultServiceGetSgTnsFeedKeyFn({ filters, limit, offset, sorting }), queryFn: () => DefaultService.getSgTnsFeed({ filters, limit, offset, sorting }) });
/**
* The handler for the Time & Sales flow summary. Aggregate: volume, premium, delta, gamma, vega
* @param data The data for the request.
* @param data.filters
* @returns HiroAggSummary Success
* @throws ApiError
*/
export const prefetchUseDefaultServiceGetSgTnsFlowSum = (queryClient: QueryClient, { filters }: {
  filters?: string;
} = {}) => queryClient.prefetchQuery({ queryKey: Common.UseDefaultServiceGetSgTnsFlowSumKeyFn({ filters }), queryFn: () => DefaultService.getSgTnsFlowSum({ filters }) });
/**
* @param data The data for the request.
* @param data.start
* @param data.end
* @returns Highlights Success
* @throws ApiError
*/
export const prefetchUseDefaultServiceGetSgTnsHighlights = (queryClient: QueryClient, { end, start }: {
  end?: number;
  start?: number;
} = {}) => queryClient.prefetchQuery({ queryKey: Common.UseDefaultServiceGetSgTnsHighlightsKeyFn({ end, start }), queryFn: () => DefaultService.getSgTnsHighlights({ end, start }) });
/**
* Health and build information for the server instance handling the request.
* Always reports the deployed commit (`repo_git_hash`). Sending a bearer token additionally
* reports whether it decoded (`able_to_decode_jwt`), and a token carrying the `administrator`
* role also reports the instance hostname plus a live reachability probe of each backing
* service (Redis cluster, users/hiro/crunchy Postgres, Scylla, S3) - any probe that fails turns
* the response into a 500 while still returning the per-service detail.
* @param data The data for the request.
* @param data.authorization Bearer <jwt-token>
* @returns ServerStatus Success
* @throws ApiError
*/
export const prefetchUseDefaultServiceGetStatus = (queryClient: QueryClient, { authorization }: {
  authorization: string;
}) => queryClient.prefetchQuery({ queryKey: Common.UseDefaultServiceGetStatusKeyFn({ authorization }), queryFn: () => DefaultService.getStatus({ authorization }) });
/**
* The handler for the HTTP request (this gets called when the HTTP GET lands at the start
* of websocket negotiation). After this completes, the actual switching from HTTP to
* websocket protocol will occur.
* This is the last point where we can extract TCP/IP metadata such as IP address of the client
* as well as things from HTTP headers such as user-agent of the browser etc.
* @param data The data for the request.
* @param data.token
* @throws ApiError
*/
export const prefetchUseDefaultServiceGetStream = (queryClient: QueryClient, { token }: {
  token: string;
}) => queryClient.prefetchQuery({ queryKey: Common.UseDefaultServiceGetStreamKeyFn({ token }), queryFn: () => DefaultService.getStream({ token }) });
/**
* @param data The data for the request.
* @param data.sym Symbol to query (e.g., NVDA)
* @returns CorrelationRegimeResponse Success
* @throws ApiError
*/
export const prefetchUseDefaultServiceGetV1CorrelationRegime = (queryClient: QueryClient, { sym }: {
  sym: string;
}) => queryClient.prefetchQuery({ queryKey: Common.UseDefaultServiceGetV1CorrelationRegimeKeyFn({ sym }), queryFn: () => DefaultService.getV1CorrelationRegime({ sym }) });
/**
* SpotGamma equity levels and vol metrics by symbol.
* For each requested symbol, the daily Equity Hub row for the trade date: key levels (call and
* put walls, key gamma/delta strikes, hedge wall), gamma and delta exposures, option volumes,
* IV/skew ranks, and per-strike gamma lists - see `EquityLevels` for the full field list.
* Symbols with no data are omitted from the response. Index-combo aliases (e.g. `S&P 500`,
* `Nasdaq`) resolve to their index's levels; `S&P ES=F` additionally shifts price-level fields
* by the index-to-futures offset.
* @param data The data for the request.
* @param data.syms Comma-separated list of symbols, e.g. `SPX,AAPL`
* @param data.date Trade date. If unspecified, defaults to today; either way the most recent payload within
* the prior 5 business days is served.
* @returns EquityLevels Map of requested symbol to its equity-levels row
* @throws ApiError
*/
export const prefetchUseDefaultServiceGetV1EquitiesBySyms = (queryClient: QueryClient, { date, syms }: {
  date?: string;
  syms: string[];
}) => queryClient.prefetchQuery({ queryKey: Common.UseDefaultServiceGetV1EquitiesBySymsKeyFn({ date, syms }), queryFn: () => DefaultService.getV1EquitiesBySyms({ date, syms }) });
/**
* @returns MoviefoneConversationSummary The caller's saved conversations, most recently updated first
* @throws ApiError
*/
export const prefetchUseDefaultServiceGetV1MoviefoneConversations = (queryClient: QueryClient) => queryClient.prefetchQuery({ queryKey: Common.UseDefaultServiceGetV1MoviefoneConversationsKeyFn(), queryFn: () => DefaultService.getV1MoviefoneConversations() });
/**
* @param data The data for the request.
* @param data.timestamp The conversation's timestamp identifier, as returned by store/list
* @returns unknown The full conversation, brotli-compressed (Content-Encoding: br) so it can be streamed straight through to the browser
* @throws ApiError
*/
export const prefetchUseDefaultServiceGetV1MoviefoneConversationsByTimestamp = (queryClient: QueryClient, { timestamp }: {
  timestamp: string;
}) => queryClient.prefetchQuery({ queryKey: Common.UseDefaultServiceGetV1MoviefoneConversationsByTimestampKeyFn({ timestamp }), queryFn: () => DefaultService.getV1MoviefoneConversationsByTimestamp({ timestamp }) });
/**
* Unusual options positioning (position moves) from the Flow Patrol Opening Setup report.
* Large opening option positions detected for the report date, enriched with premiums,
* open-interest changes, IV/skew ranks, and probability of payoff, ordered by absolute
* premium (largest first). Multileg legs are included and share a multileg_id.
* @param data The data for the request.
* @param data.date Report date. If unspecified, the most recent date with data is served.
* @returns PositionMovesRsp Position moves (unusual flows) for the report date, ordered by absolute premium
* @throws ApiError
*/
export const prefetchUseDefaultServiceGetV1OpeningSetupPositionMoves = (queryClient: QueryClient, { date }: {
  date?: string;
} = {}) => queryClient.prefetchQuery({ queryKey: Common.UseDefaultServiceGetV1OpeningSetupPositionMovesKeyFn({ date }), queryFn: () => DefaultService.getV1OpeningSetupPositionMoves({ date }) });
/**
* Setup Profiles picks from the Flow Patrol Opening Setup report.
* Four vol-surface-screened trade-idea picks for the report date - Bull (flattest put
* skew), Bear (steepest put skew), Short Vol (highest IV/RV), and Long Vol (lowest
* IV/RV) - each a single symbol from the liquid, earnings-clean universe with its
* screening metrics and a human-readable play description.
* @param data The data for the request.
* @param data.date Report date. If unspecified, the most recent date with data is served.
* @returns SetupProfilesRsp Setup Profiles picks for the report date
* @throws ApiError
*/
export const prefetchUseDefaultServiceGetV1OpeningSetupProfiles = (queryClient: QueryClient, { date }: {
  date?: string;
} = {}) => queryClient.prefetchQuery({ queryKey: Common.UseDefaultServiceGetV1OpeningSetupProfilesKeyFn({ date }), queryFn: () => DefaultService.getV1OpeningSetupProfiles({ date }) });
/**
* Implied-vol scanner from the Flow Patrol Opening Setup report.
* The most active option names for the report date (ranked by option volume) with current
* and prior front-month IV, day-over-day IV change, IV rank/percentile vs trailing
* history, and a practical-read classification of the vol setup.
* @param data The data for the request.
* @param data.date Report date. If unspecified, the most recent date with data is served.
* @returns VolScannerRsp Most-active-options vol scanner for the report date, ordered by option volume
* @throws ApiError
*/
export const prefetchUseDefaultServiceGetV1OpeningSetupVolScanner = (queryClient: QueryClient, { date }: {
  date?: string;
} = {}) => queryClient.prefetchQuery({ queryKey: Common.UseDefaultServiceGetV1OpeningSetupVolScannerKeyFn({ date }), queryFn: () => DefaultService.getV1OpeningSetupVolScanner({ date }) });
/**
* @param data The data for the request.
* @param data.symbol
* @returns SettlementStyleResponse Exercise style per expiry date for one underlying
* @throws ApiError
*/
export const prefetchUseDefaultServiceGetV1SettlementStyle = (queryClient: QueryClient, { symbol }: {
  symbol: string;
}) => queryClient.prefetchQuery({ queryKey: Common.UseDefaultServiceGetV1SettlementStyleKeyFn({ symbol }), queryFn: () => DefaultService.getV1SettlementStyle({ symbol }) });
/**
* @param data The data for the request.
* @param data.riskFreeRate
* @returns SpotlineTopTradesRsp Top-ranked trade for each of today's Opening Setup profiles
* @throws ApiError
*/
export const prefetchUseDefaultServiceGetV1SpotlineTopTrades = (queryClient: QueryClient, { riskFreeRate }: {
  riskFreeRate: number;
}) => queryClient.prefetchQuery({ queryKey: Common.UseDefaultServiceGetV1SpotlineTopTradesKeyFn({ riskFreeRate }), queryFn: () => DefaultService.getV1SpotlineTopTrades({ riskFreeRate }) });
/**
* @param data The data for the request.
* @param data.sym If unspecified, defaults to SPX
* @param data.date If unspecified, defaults to SPX
* @param data.mktClose If unspecified, defaults to true
* @returns unknown message-packed expiry strike map
* @throws ApiError
*/
export const prefetchUseDefaultServiceGetV2DailyGreeks = (queryClient: QueryClient, { date, mktClose, sym }: {
  date?: string;
  mktClose?: boolean;
  sym?: string;
} = {}) => queryClient.prefetchQuery({ queryKey: Common.UseDefaultServiceGetV2DailyGreeksKeyFn({ date, mktClose, sym }), queryFn: () => DefaultService.getV2DailyGreeks({ date, mktClose, sym }) });
/**
* @param data The data for the request.
* @param data.sym
* @returns unknown message-packed expiry strike map
* @throws ApiError
*/
export const prefetchUseDefaultServiceGetV2LatestGreeks = (queryClient: QueryClient, { sym }: {
  sym: string;
}) => queryClient.prefetchQuery({ queryKey: Common.UseDefaultServiceGetV2LatestGreeksKeyFn({ sym }), queryFn: () => DefaultService.getV2LatestGreeks({ sym }) });
/**
* @param data The data for the request.
* @param data.symbol
* @param data.mktActor
* @param data.date
* @param data.ts
* @returns unknown Success
* @throws ApiError
*/
export const prefetchUseDefaultServiceGetV2OpenInterestIntradayDelta = (queryClient: QueryClient, { date, mktActor, symbol, ts }: {
  date?: string;
  mktActor: MarketActorKind;
  symbol: string;
  ts?: string;
}) => queryClient.prefetchQuery({ queryKey: Common.UseDefaultServiceGetV2OpenInterestIntradayDeltaKeyFn({ date, mktActor, symbol, ts }), queryFn: () => DefaultService.getV2OpenInterestIntradayDelta({ date, mktActor, symbol, ts }) });
/**
* @param data The data for the request.
* @param data.symbol
* @param data.mktActor
* @param data.date
* @param data.ts
* @returns unknown Success
* @throws ApiError
*/
export const prefetchUseDefaultServiceGetV2OpenInterestIntradayGamma = (queryClient: QueryClient, { date, mktActor, symbol, ts }: {
  date?: string;
  mktActor: MarketActorKind;
  symbol: string;
  ts?: string;
}) => queryClient.prefetchQuery({ queryKey: Common.UseDefaultServiceGetV2OpenInterestIntradayGammaKeyFn({ date, mktActor, symbol, ts }), queryFn: () => DefaultService.getV2OpenInterestIntradayGamma({ date, mktActor, symbol, ts }) });
/**
* @param data The data for the request.
* @param data.sym
* @param data.date
* @returns unknown Success
* @throws ApiError
*/
export const prefetchUseDefaultServiceGetV2OpenInterestIntradayStats = (queryClient: QueryClient, { date, sym }: {
  date?: string;
  sym: string;
}) => queryClient.prefetchQuery({ queryKey: Common.UseDefaultServiceGetV2OpenInterestIntradayStatsKeyFn({ date, sym }), queryFn: () => DefaultService.getV2OpenInterestIntradayStats({ date, sym }) });
/**
* @param data The data for the request.
* @param data.barType
* @param data.symbol
* @param data.date
* @returns unknown Success
* @throws ApiError
*/
export const prefetchUseDefaultServiceGetV2OpenInterestIntradayStrikeBars = (queryClient: QueryClient, { barType, date, symbol }: {
  barType: IntradayBarType;
  date?: string;
  symbol?: string;
}) => queryClient.prefetchQuery({ queryKey: Common.UseDefaultServiceGetV2OpenInterestIntradayStrikeBarsKeyFn({ barType, date, symbol }), queryFn: () => DefaultService.getV2OpenInterestIntradayStrikeBars({ barType, date, symbol }) });
/**
* @param data The data for the request.
* @param data.greek
* @param data.mktActor
* @param data.symbol
* @param data.date
* @returns unknown Success
* @throws ApiError
*/
export const prefetchUseDefaultServiceGetV2OpenInterestIntradayTimestamps = (queryClient: QueryClient, { date, greek, mktActor, symbol }: {
  date?: string;
  greek: IntradayGreek;
  mktActor: MarketActorKind;
  symbol?: string;
}) => queryClient.prefetchQuery({ queryKey: Common.UseDefaultServiceGetV2OpenInterestIntradayTimestampsKeyFn({ date, greek, mktActor, symbol }), queryFn: () => DefaultService.getV2OpenInterestIntradayTimestamps({ date, greek, mktActor, symbol }) });
/**
* A simple handler for validating and inspecting a JWT token
* @returns Claims Success
* @throws ApiError
*/
export const prefetchUseDefaultServiceGetValidateBearer = (queryClient: QueryClient) => queryClient.prefetchQuery({ queryKey: Common.UseDefaultServiceGetValidateBearerKeyFn(), queryFn: () => DefaultService.getValidateBearer() });
/**
* @param data The data for the request.
* @param data.dates
* @returns Page_VixQuote Success
* @throws ApiError
*/
export const prefetchUseDefaultServiceGetVixQuote = (queryClient: QueryClient, { dates }: {
  dates?: string[];
} = {}) => queryClient.prefetchQuery({ queryKey: Common.UseDefaultServiceGetVixQuoteKeyFn({ dates }), queryFn: () => DefaultService.getVixQuote({ dates }) });
